import type { z } from 'zod';

import { getStructuralPath } from '@fragment/path-utils/path';
import { MAX_ENTRY_LINES } from '@fragment/types/entry';
import { makePaymentEntryTypePrefix } from '@fragment/types/paymentEntryType';
import type { BaseSchemaPayments } from '@fragment/types/schemas';
import {
  PaymentAccountingEventKey,
  PaymentTypeDirection,
  SYSTEM_LINE_AMOUNT,
  SYSTEM_PARAMETER_NAMES,
  SystemLineKind,
} from '@fragment/types/schemas';
import type { SafeString } from '@fragment/types/strings';
import {
  getStringParameters,
  ParameterizedAmount,
} from '@fragment/types/strings';
import { safeGetZodErrors } from '@fragment/zod-utils/src';

import type { transformAndValidateCoA } from './coa';
import type { ValidatedAccount } from './entries';
import { getSchemaObjectParameters } from '../../parameterization';
import {
  AccountCategory,
  AccountCategoryMutualExclusionError,
  getSchemaAccountCategory,
} from '../accountCategory';
import {
  groupByMap,
  validateBalancedLines,
  validateDescription,
  validateLine,
  validateNonEmptySafeString,
  validateUniqueByKey,
} from './entries';
import { getStructuralPathToAccount } from './utilities';

type ValidationResult = {
  code?: string;
  message: string;
  path: (string | number)[];
};

type PaymentsInput = z.infer<typeof BaseSchemaPayments>;

/**
 * A payment entry line as the validators consume it. Every field is optional
 * because the designer validates drafts before they could parse as API input.
 * Ledger-only line fields (tx, tags, repeated) are absent — every input shape
 * (GraphQL, JSON schema, zod) rejects them at the type level.
 */
export type PaymentLineValidationInput = {
  key?: string;
  account?: { path: string };
  amount?: string;
  currency?: { code: string; customCurrencyId?: string };
  description?: string;
  system?: SystemLineKind;
};

export type PaymentEntryValidationInput = {
  description?: string;
  lines?: PaymentLineValidationInput[];
};

/**
 * A payment type as the validators consume it. Unlike the parsed API input,
 * every accounting entry is optional — a draft may not have created its
 * settled entry yet; validatePaymentType reports the gap as an error.
 */
export type PaymentTypeValidationInput = {
  type: string;
  typeVersion: number;
  payment: { amount: string; direction: PaymentTypeDirection };
  accounting: {
    [event in PaymentAccountingEventKey]?: PaymentEntryValidationInput;
  };
};

type ValidatePaymentsParams = {
  payments: PaymentsInput;
  validatedCoA: ReturnType<typeof transformAndValidateCoA>['filledIn'];
};

// Only the exact canonical string is accepted — equivalent expressions
// (reordered terms, extra whitespace) buy nothing and widen the surface a
// validation bug could hide in. The designer writes and locks the exact
// string, so no legitimate producer needs the tolerance.
const amountMatchesSystemLine = (
  amount: string | undefined,
  system: SystemLineKind
): boolean => amount === SYSTEM_LINE_AMOUNT[system];

const getAccountCategoryOrUndefined = (account: ValidatedAccount) => {
  try {
    return getSchemaAccountCategory(account);
  } catch (e) {
    // validateAccountCategory already reports the multi category error
    if (e instanceof AccountCategoryMutualExclusionError) {
      return undefined;
    }
    throw e;
  }
};

export const findPaymentAccountPaths = (
  accountPathToAccount: Map<string, ValidatedAccount>
): string[] =>
  Array.from(accountPathToAccount.entries())
    .filter(
      ([, account]) =>
        getAccountCategoryOrUndefined(account) === AccountCategory.Payment
    )
    .map(([path]) => path);

export const findPaymentAccountPath = (
  accountPathToAccount: Map<string, ValidatedAccount>
): string | undefined => findPaymentAccountPaths(accountPathToAccount)[0];

const fillInPaymentEntryLines = (
  entry: PaymentEntryValidationInput,
  accountPathToAccount: Map<string, ValidatedAccount>
) =>
  (entry.lines ?? []).map((line, idx) => {
    const path = line.account?.path
      ? getStructuralPath(line.account.path)
      : undefined;
    return {
      ...line,
      account: line.account ?? { path: '' },
      key: line.key ?? `line-${idx}`,
      amount: line.amount ?? '',
      currency:
        line.currency ??
        (path ? accountPathToAccount.get(path)?.currency : undefined),
    };
  });

type ValidatePaymentEntryParams = {
  entry: PaymentEntryValidationInput;
  event: PaymentAccountingEventKey;
  /** `{type}.{typeVersion}.{event}`, used in error messages only. */
  entryIdentity: string;
  accountPathToAccount: Map<string, ValidatedAccount>;
  paymentAccountPath: string | undefined;
};

const systemLinesByPaymentAccountingEvent = {
  [PaymentAccountingEventKey.initiated]: [],
  [PaymentAccountingEventKey.settled]: [
    SystemLineKind.payment_settlement_line,
    SystemLineKind.payment_fee_line,
  ],
  [PaymentAccountingEventKey.refund_initiated]: [
    SystemLineKind.payment_refund_line,
  ],
  [PaymentAccountingEventKey.refund_failed]: [
    SystemLineKind.payment_refund_reversal_line,
  ],
} as const satisfies Record<
  PaymentAccountingEventKey,
  readonly SystemLineKind[]
>;

const hasSystemLines = (event: PaymentAccountingEventKey): boolean =>
  systemLinesByPaymentAccountingEvent[event].length > 0;

const validateSystemLines = ({
  lines,
  event,
  entryIdentity,
  paymentAccountPath,
}: Pick<
  ValidatePaymentEntryParams,
  'event' | 'entryIdentity' | 'paymentAccountPath'
> & {
  lines: ReturnType<typeof fillInPaymentEntryLines>;
}): ValidationResult[] => {
  const errors: ValidationResult[] = [];
  const requiredSystemLines = systemLinesByPaymentAccountingEvent[event];
  const requiredSystemLineSet: ReadonlySet<SystemLineKind> = new Set(
    requiredSystemLines
  );

  requiredSystemLines.forEach((system) => {
    const count = lines.filter((line) => line.system === system).length;
    if (count !== 1) {
      errors.push({
        path: ['lines'],
        message: `Payment entry (${entryIdentity}) must have exactly one line with system '${system}', found ${count}.`,
      });
    }
  });

  lines.forEach((line, idx) => {
    const isPaymentAccountLine =
      paymentAccountPath !== undefined &&
      getStructuralPath(line.account.path) === paymentAccountPath;
    if (line.system) {
      if (!requiredSystemLineSet.has(line.system)) {
        errors.push({
          path: ['lines', idx, 'system'],
          message: `System line '${line.system}' is not allowed on the '${event}' entry. (payment entry: ${entryIdentity} | line key: ${line.key})`,
        });
        return;
      }
      if (paymentAccountPath !== undefined && !isPaymentAccountLine) {
        errors.push({
          path: ['lines', idx, 'account', 'path'],
          message: `System line '${line.system}' must post to the Payment Account. (payment entry: ${entryIdentity} | line key: ${line.key})`,
        });
      }
      if (!amountMatchesSystemLine(line.amount, line.system)) {
        errors.push({
          path: ['lines', idx, 'amount'],
          message: `System line '${line.system}' must have amount '${
            SYSTEM_LINE_AMOUNT[line.system]
          }'. (payment entry: ${entryIdentity} | line key: ${line.key})`,
        });
      }
    } else if (hasSystemLines(event) && isPaymentAccountLine) {
      errors.push({
        path: ['lines', idx, 'system'],
        message: `Only system lines may post to the Payment Account. (payment entry: ${entryIdentity} | line key: ${line.key})`,
      });
    }
  });

  return errors;
};

type FilledPaymentLine = ReturnType<typeof fillInPaymentEntryLines>[number];

/**
 * Validates entry for system parameter usage.
 *
 * System parameters are runtime-filled, never caller-supplied, so referencing
 * one is only meaningful where a system line posts it and user lines must
 * reference it back to balance. Anywhere else the name would shadow a caller
 * parameter that can never be provided. Descriptions are parameterized at
 * runtime too, so they follow the same rule.
 */
const validateSystemParameterUsage = ({
  entry,
  lines,
  entryIdentity,
}: Pick<ValidatePaymentEntryParams, 'entry' | 'entryIdentity'> & {
  lines: FilledPaymentLine[];
}): ValidationResult[] => {
  /**
   * System params allowed in user-defined content: a system param may be
   * referenced only when a system line on this entry uses it. Otherwise it
   * cannot be used.
   */
  const allowedSystemParams = new Set(
    lines.flatMap((line) =>
      line.system ? getStringParameters(SYSTEM_LINE_AMOUNT[line.system]) : []
    )
  );
  const findIllegalSystemParams = (obj: unknown): string[] =>
    getSchemaObjectParameters(obj).filter(
      (param) =>
        SYSTEM_PARAMETER_NAMES.has(param) && !allowedSystemParams.has(param)
    );
  const illegalParamMessage = (param: string, lineKey?: string) =>
    `System parameter '${param}' can only be used on an entry whose system lines reference it. (payment entry: ${entryIdentity}${
      lineKey === undefined ? '' : ` | line key: ${lineKey}`
    })`;

  const errors: ValidationResult[] = [];
  findIllegalSystemParams(entry.description).forEach((param) => {
    errors.push({
      path: ['description'],
      message: illegalParamMessage(param),
    });
  });
  lines.forEach((line, idx) => {
    // System lines are fully validated by validateSystemLines.
    if (line.system) return;
    const { account, amount, description } = line;
    findIllegalSystemParams({ account, amount, description }).forEach(
      (param) => {
        errors.push({
          path: ['lines', idx],
          message: illegalParamMessage(param, line.key),
        });
      }
    );
  });
  return errors;
};

export const validatePaymentEntry = ({
  entry,
  event,
  entryIdentity,
  accountPathToAccount,
  paymentAccountPath,
}: ValidatePaymentEntryParams): ValidationResult[] => {
  const errors: ValidationResult[] = [];

  errors.push(...validateDescription(entry.description));

  const lines = fillInPaymentEntryLines(entry, accountPathToAccount);
  if (lines.length > MAX_ENTRY_LINES) {
    errors.push({
      path: ['lines'],
      message: `Payment entry (${entryIdentity}) has too many lines. The maximum allowed is ${MAX_ENTRY_LINES}, but this entry has ${lines.length}.`,
    });
  }

  const allowPaymentAccount = hasSystemLines(event);
  errors.push(
    ...lines.flatMap((line, idx) => {
      const lineErrors: ValidationResult[] = [];
      const account = accountPathToAccount.get(
        getStructuralPath(line.account.path)
      );
      if (
        account &&
        getAccountCategoryOrUndefined(account) === AccountCategory.Linked
      ) {
        lineErrors.push({
          path: ['account', 'path'],
          message: `Payment entry lines cannot post to Linked Accounts. (payment entry: ${entryIdentity} | line key: ${line.key})`,
        });
      }
      lineErrors.push(
        // validateLine requires a tx match on lines posting to linked
        // accounts, and payment lines (which have no tx field) cannot
        // satisfy it. The linked-account error above already covers that
        // case, so the tx-path errors are dropped.
        ...validateLine(
          entryIdentity,
          undefined,
          lines.length === 2,
          false,
          line,
          accountPathToAccount,
          { allowPaymentAccount }
        ).filter(({ path }) => path[0] !== 'tx')
      );
      return lineErrors.map(({ path, ...rest }) => ({
        path: ['lines', idx, ...path],
        ...rest,
      }));
    })
  );

  errors.push(
    ...validateUniqueByKey({
      key: 'key',
      items: lines,
      pathPrefix: ['lines'],
      getErrorMessage: (item) =>
        `Payment entry (${entryIdentity}) already has a line with key '${item.key}'`,
    })
  );

  errors.push(
    ...validateBalancedLines(
      entryIdentity,
      undefined,
      lines,
      accountPathToAccount,
      {
        allowPaymentAccount,
      }
    )
  );

  errors.push(
    ...validateSystemLines({ lines, event, entryIdentity, paymentAccountPath })
  );

  errors.push(...validateSystemParameterUsage({ entry, lines, entryIdentity }));

  return errors;
};

type ValidatePaymentTypeParams = {
  paymentType: PaymentTypeValidationInput;
  accountPathToAccount: Map<string, ValidatedAccount>;
  paymentAccountPath: string | undefined;
};

export const validatePaymentType = ({
  paymentType,
  accountPathToAccount,
  paymentAccountPath,
}: ValidatePaymentTypeParams): ValidationResult[] => {
  const errors = validateNonEmptySafeString({
    value: paymentType.type,
    path: ['type'],
    name: 'type',
    entityName: 'Payment Type',
  });

  if (paymentType.typeVersion !== 1) {
    errors.push({
      path: ['typeVersion'],
      message: `Payment Type (${paymentType.type}) must have typeVersion 1.`,
    });
  }
  if (paymentType.payment.direction !== PaymentTypeDirection.payin) {
    errors.push({
      path: ['payment', 'direction'],
      message: `Payment Type (${paymentType.type}) must have direction '${PaymentTypeDirection.payin}'. Direction '${PaymentTypeDirection.payout}' is not yet supported.`,
    });
  }
  // ParameterizedAmount accepts blank strings, so require the amount first.
  if (!paymentType.payment.amount.trim()) {
    errors.push({
      path: ['payment', 'amount'],
      message: `Payment Type (${paymentType.type}) must specify a payment amount.`,
    });
  } else {
    errors.push(
      ...safeGetZodErrors(ParameterizedAmount(), paymentType.payment.amount, [
        'payment',
        'amount',
      ])
    );
    // The payment amount is what the runtime derives the system parameters
    // from, so referencing one here would be circular.
    getSchemaObjectParameters(paymentType.payment.amount)
      .filter((param) => SYSTEM_PARAMETER_NAMES.has(param))
      .forEach((param) => {
        errors.push({
          path: ['payment', 'amount'],
          message: `Payment Type (${paymentType.type}) payment amount cannot use the system parameter '${param}'.`,
        });
      });
  }

  Object.keys(paymentType.accounting)
    .filter((event) => !(event in PaymentAccountingEventKey))
    .forEach((event) => {
      errors.push({
        path: ['accounting', event],
        message: `Payment Type (${
          paymentType.type
        }) has unknown accounting event '${event}'. Supported events: ${Object.values(
          PaymentAccountingEventKey
        ).join(', ')}.`,
      });
    });

  // Required in the API input; drafts may not have created it yet.
  if (!paymentType.accounting.settled) {
    errors.push({
      path: ['accounting', PaymentAccountingEventKey.settled],
      message: `Payment Type (${paymentType.type}) must define a '${PaymentAccountingEventKey.settled}' entry.`,
    });
  }

  errors.push(
    ...Object.values(PaymentAccountingEventKey).flatMap((event) => {
      const entry = paymentType.accounting[event];
      if (!entry) {
        return [];
      }
      return validatePaymentEntry({
        entry,
        event,
        entryIdentity: `${paymentType.type}.${paymentType.typeVersion}.${event}`,
        accountPathToAccount,
        paymentAccountPath,
      }).map(({ path, ...rest }) => ({
        path: ['accounting', event, ...path],
        ...rest,
      }));
    })
  );

  return errors;
};

/**
 * Duplicate-type errors for a list of payment types, one per member of each
 * colliding group.
 *
 * Identity is type plus typeVersion, matching the stored shape where a type's
 * versions are separate entries in the list. Callers holding draft entities,
 * which own their versions, pass the version the entity is pinned to.
 */
export const validateUniquePaymentTypes = <
  T extends { type: string; typeVersion: number }
>({
  types,
  pathPrefix,
}: {
  types: readonly T[];
  pathPrefix: (string | number)[];
}): ValidationResult[] =>
  Array.from(
    groupByMap(
      types.map((paymentType, idx) => [paymentType, idx] as const),
      ([paymentType]) => `${paymentType.type}:${paymentType.typeVersion}`
    ).values()
  )
    .filter((group) => group.length > 1)
    .flatMap((group) =>
      group.map(([paymentType, idx]) => ({
        message: `Payment Types cannot have duplicate types '${paymentType.type}'`,
        path: [...pathPrefix, idx, 'type'],
      }))
    );

export const validateReservedPaymentEntryTypes = ({
  ledgerEntryTypes,
  paymentTypes,
}: {
  ledgerEntryTypes: readonly { type: SafeString }[];
  paymentTypes: readonly { type: SafeString }[];
}): ValidationResult[] => {
  const reservedNamespaces = paymentTypes.map(({ type }) => ({
    paymentType: type,
    prefix: makePaymentEntryTypePrefix(type),
  }));

  return ledgerEntryTypes.flatMap((entry, idx) => {
    const reservedBy = reservedNamespaces.find(({ prefix }) =>
      entry.type.startsWith(prefix)
    );
    return reservedBy === undefined
      ? []
      : [
          {
            path: ['types', idx, 'type'],
            message: `Entry (${entry.type}) is in the '${reservedBy.prefix}' namespace reserved for the entries Payment Type (${reservedBy.paymentType}) posts. Rename one of them.`,
          },
        ];
  });
};

export const transformAndValidatePayments = ({
  payments,
  validatedCoA,
}: ValidatePaymentsParams) => {
  const accountPathToAccount = getStructuralPathToAccount(validatedCoA);
  const paymentAccountPath = findPaymentAccountPath(accountPathToAccount);

  const errors: ValidationResult[] = [];

  if (payments.types.length > 0 && paymentAccountPath === undefined) {
    errors.push({
      path: ['types'],
      message: `Schema must contain a Payment Account to define Payment Types.`,
    });
  }

  errors.push(
    ...validateUniquePaymentTypes({
      types: payments.types,
      pathPrefix: ['types'],
    })
  );

  errors.push(
    ...payments.types.flatMap((paymentType, idx) =>
      validatePaymentType({
        paymentType,
        accountPathToAccount,
        paymentAccountPath,
      }).map(({ path, ...rest }) => ({
        path: ['types', idx, ...path],
        ...rest,
      }))
    )
  );

  return {
    filledIn: payments,
    errors,
  };
};
